/**
 * QRWidget — compacte QR-generator voor het dashboard. Vult de widget: invoer boven,
 * live preview in het midden, download-knoppen onderaan. Standaard vierkante stijl.
 * PNG exporteert groot met bestandsnaam "QR-code [website]". De volledige generator
 * open je via de knop rechtsboven in de header (WidgetShell fullPageHref).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import QRCodeStyling from 'qr-code-styling';
import toast from 'react-hot-toast';
import { T } from './widgetTokens';
import { useFitSquare } from './gameKit';

const hostOf = (v) => {
  try { return new URL(/^https?:\/\//.test(v) ? v : `https://${v}`).hostname.replace(/^www\./, ''); }
  catch { return ''; }
};

const PNG_EXPORT = 1024;

export default function QRWidget() {
  const [data, setData] = useState('https://intern.fullframe.nl');
  const containerRef = useRef(null);
  const qrRef = useRef(null);
  const [fitRef, size] = useFitSquare();

  const opts = useMemo(() => ({
    width: 300, height: 300, type: 'svg', data: data || ' ', margin: 8,
    qrOptions: { errorCorrectionLevel: 'Q' },
    dotsOptions: { color: '#2d3335', type: 'square' },
    backgroundOptions: { color: '#ffffff' },
    cornersSquareOptions: { color: '#2d3335', type: 'square' },
    cornersDotOptions: { color: '#2d3335', type: 'square' },
  }), [data]);

  useEffect(() => {
    qrRef.current = new QRCodeStyling(opts);
    if (containerRef.current) { containerRef.current.innerHTML = ''; qrRef.current.append(containerRef.current); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { qrRef.current?.update(opts); }, [opts]);

  const download = (ext) => {
    if (!data.trim()) { toast.error('Vul eerst inhoud in'); return; }
    const host = hostOf(data);
    const name = `QR-code ${host || 'code'}`;
    if (ext === 'png') {
      qrRef.current?.update({ ...opts, width: PNG_EXPORT, height: PNG_EXPORT });
      setTimeout(() => { qrRef.current?.download({ name, extension: 'png' }); qrRef.current?.update(opts); }, 60);
    } else {
      qrRef.current?.download({ name, extension: 'svg' });
    }
  };

  const Btn = ({ ext }) => (
    <Box onClick={() => download(ext)} sx={{
      flex: 1, textAlign: 'center', cursor: 'pointer', px: 1, py: 0.7, borderRadius: '0.5rem',
      bgcolor: T.surfaceLow, fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.74rem', color: T.secondary,
      '&:hover': { bgcolor: T.surfaceHigh, color: T.tertiary },
    }}>{ext.toUpperCase()}</Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box component="input" value={data} onChange={(e) => setData(e.target.value)} placeholder="URL of tekst"
        sx={{ flexShrink: 0, width: '100%', bgcolor: T.surfaceLow, border: 'none', borderRadius: '0.5rem', px: 1.25, py: 0.85, color: T.text, outline: 'none', fontFamily: T.fontBody, fontSize: '0.8rem', '&:focus': { boxShadow: `0 0 0 2px ${T.tertiary}40` } }} />

      <Box ref={fitRef} sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box ref={containerRef} sx={{ width: size, height: size, '& svg': { display: 'block', width: '100%', height: '100%', borderRadius: '0.5rem' } }} />
      </Box>

      <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.75 }}>
        <Btn ext="png" />
        <Btn ext="svg" />
      </Box>
    </Box>
  );
}
